import {
  SkyBadge,
  SkyButton,
  SkyDataGrid,
  SkyDialog,
  SkyDrawer,
  SkyLink,
  SkyText,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "wouter";
import {
  DECISION_NEEDED_STATUSES,
  fittingDecisions,
  type SchemaChangeView,
  type SchemaDecision,
} from "@shared/types/schemaChanges";
import { StatusCell } from "@/components/runs/StatusCell";
import { SingleCheckbox } from "@/components/ui/SingleCheckbox";
import { useDecideSchemaChanges, useSchemaChanges } from "@/hooks/use-schema-changes";
import { InlineErrorBar, useInlineError, type InlineFailure } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { LIST_PAGE_SIZE, LIST_PAGE_SIZE_OPTIONS } from "@/lib/grid";
import { absolutePath } from "@/lib/routes";
import {
  SCHEMA_DECISION_LABELS,
  SCHEMA_KIND_HELP,
  SCHEMA_KIND_LABELS,
  SCHEMA_STATUS_PRESENTATION,
  decidedSentence,
  runNotStartedSentence,
  typesText,
} from "./schemaChangeCopy";
import { formatInstant, instantHoverTitle } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * A table's schema change log (product gaps row 7, design section 4.5): a drawer, because it is a
 * detail panel that keeps the connection in view (rules.md §5). Newest first, 20 rows a page with
 * 10, 20 and 50, the log's own total. Rows that need a decision can be ticked; the actions offered
 * are exactly the ones every ticked row fits (`fittingDecisions`, the rule the route enforces).
 *
 * Widen and Rebuild ask first, each in a dialog that says what the next import does to the table.
 * "Run this table now" (only with `runs.start`) starts that import through the same route.
 * Without `dataflows.write` the log is read-only: no ticks and no actions.
 */

export interface SchemaChangeDrawerProps {
  open: boolean;
  onClose: () => void;
  dataflowId: string;
  connectionName: string;
  /** One table's log; null lists every table of the connection. */
  entityName: string | null;
  canWrite: boolean;
  canStart: boolean;
  /**
   * Round 6 (T-02): the view the log opens on. A run's "Schema changed at source" link opens
   * `all`, because a change the extractor saw that needs no decision is `applied`, not open.
   */
  initialStatus?: "open" | "all";
  /**
   * #1663: the run a link from its page came from. The log then opens on "This import" (the
   * changes that run first saw), with Open and All one click away.
   */
  runId?: string | null;
}

type LogView = "import" | "open" | "all";

const VIEW_LABELS: Record<LogView, string> = { import: "This import", open: "Open", all: "All" };

type ConfirmKind = Extract<SchemaDecision, "widen" | "rebuild">;

const DECIDABLE = new Set<string>([...DECISION_NEEDED_STATUSES, "ignored"]);

/** "orders", or "3 tables" when the ticked changes span several tables. */
function tablesLabel(tables: readonly string[]): string | undefined {
  return tables.length === 1 ? tables[0] : `${tables.length} tables`;
}

/** The decisions every ticked change fits, and Run this table now when one can start it. */
function DecisionBar({
  decisions,
  tableCount,
  canStart,
  runNow,
  onRunNowChange,
  decisionButton,
}: {
  decisions: SchemaDecision[];
  tableCount: number;
  canStart: boolean;
  runNow: boolean;
  onRunNowChange: (runNow: boolean) => void;
  decisionButton: (decision: SchemaDecision) => ReactNode;
}) {
  return (
    <fieldset
      className="flex min-w-0 flex-wrap items-center gap-2"
      aria-label="Decide on the selected changes"
      data-testid="schema-decisions"
    >
      {decisions.length > 0 ? (
        decisions.map(decisionButton)
      ) : (
        <SkyText size={200} color="muted" testId="schema-decisions-none">
          No one decision fits every selected change. Select changes of one kind.
        </SkyText>
      )}
      {canStart && decisions.length > 0 ? (
        <SingleCheckbox
          label={tableCount > 1 ? "Run these tables now" : "Run this table now"}
          checked={runNow}
          onChange={onRunNowChange}
          testId="schema-run-now"
        />
      ) : null}
    </fieldset>
  );
}

/** Widen and Rebuild ask first: what the next import does to the table. */
function ConfirmDecisionDialog({
  confirming,
  chosen,
  tableLabel,
  rebuildRunsNow,
  pending,
  error,
  onConfirm,
  onCancel,
}: {
  confirming: ConfirmKind;
  chosen: SchemaChangeView[];
  tableLabel: string | undefined;
  /** Run this table now is ticked (and may be): the rebuild starts at once. */
  rebuildRunsNow: boolean;
  pending: boolean;
  error: InlineFailure | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  // The column a single widen names, else how many columns it widens.
  const widenWhat = chosen.length === 1 ? chosen[0]?.columnName : `${chosen.length} columns`;
  const widerTypes = chosen.length === 1 ? "the wider type" : "the wider types";
  return (
    <SkyDialog
      open
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
      title={confirming === "rebuild" ? `Rebuild ${tableLabel}?` : `Widen ${widenWhat}?`}
      size="medium"
      testId={`schema-confirm-${confirming}`}
      onBeforeClose={(reason) => reason !== "primaryAction"}
      primaryAction={{
        label: confirming === "rebuild" ? "Rebuild table" : "Widen column",
        onClick: onConfirm,
        loading: pending,
        disabled: pending,
      }}
      secondaryAction={{ label: "Cancel", onClick: onCancel }}
    >
      <div className="flex flex-col gap-4">
        <SkyText as="p" size={300} block testId={`schema-confirm-${confirming}-copy`}>
          {confirming === "rebuild"
            ? "Every row is read again from the source and the table is replaced when the load finishes. Reports keep reading the current table until then."
            : `The table is copied with ${widerTypes} and swapped in at the start of its next import, keeping its grants. Reports keep reading the current table until then.`}
        </SkyText>
        {confirming === "rebuild" && !rebuildRunsNow ? (
          <SkyText as="p" size={200} color="muted" block>
            A table read incrementally is rebuilt by its next full read. Tick Run this table now to
            start one.
          </SkyText>
        ) : null}
        <InlineErrorBar
          error={error}
          title={error?.retryable ? undefined : "The decision was not recorded"}
          testId="schema-confirm-error"
        />
      </div>
    </SkyDialog>
  );
}

function emptyLogMessage(view: LogView, status: "open" | "all"): string {
  if (view === "import") return "This import found no column change.";
  if (status === "open") return "No schema change needs a decision.";
  return "No schema change has been seen yet.";
}

function logQuery(args: {
  status: "open" | "all";
  page: number;
  pageSize: number;
  entityName: string | null;
  view: LogView;
  runId?: string | null;
}) {
  const { status, page, pageSize, entityName, view, runId } = args;
  return {
    status,
    page,
    pageSize,
    ...(entityName ? { entity: entityName } : {}),
    ...(view === "import" && runId ? { run: runId } : {}),
  };
}

function announceDecision(
  notice: ReturnType<typeof useNotice>,
  decided: string,
  result: { runNotStarted?: { message: string } | null },
): void {
  if (result.runNotStarted) {
    notice.warning(runNotStartedSentence(decided, result.runNotStarted.message));
  } else {
    notice.success(decided);
  }
}

export function SchemaChangeDrawer({
  open,
  onClose,
  dataflowId,
  connectionName,
  entityName,
  canWrite,
  canStart,
  initialStatus = "open",
  runId = null,
}: SchemaChangeDrawerProps) {
  const zone = useDisplayTz();
  const [view, setView] = useState<LogView>(runId ? "import" : initialStatus);
  // "This import" lists every status of that run's changes, so an applied one shows too.
  const status: "open" | "all" = view === "open" ? "open" : "all";
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(LIST_PAGE_SIZE);
  const [selected, setSelected] = useState<string[]>([]);
  const [runNow, setRunNow] = useState(false);
  const [confirming, setConfirming] = useState<ConfirmKind | null>(null);
  const inline = useInlineError();
  const notice = useNotice();
  const decide = useDecideSchemaChanges(dataflowId);
  const log = useSchemaChanges(
    dataflowId,
    logQuery({ status, page, pageSize, entityName, view, runId }),
    open,
  );
  const rows = useMemo(() => log.data?.items ?? [], [log.data]);
  // The kinds on this page that need a word of explanation (#1663).
  const presentKinds = (["added", "dropped"] as const).filter((kind) =>
    rows.some((row) => row.kind === kind),
  );
  const chosen = rows.filter((row) => selected.includes(row.id));
  const decisions = canWrite ? fittingDecisions(chosen) : [];
  const tables = [...new Set(chosen.map((row) => row.entityName))];
  const tableLabel = entityName ?? tablesLabel(tables);

  const submit = async (decision: SchemaDecision): Promise<void> => {
    inline.clear();
    try {
      const result = await decide.mutateAsync({
        changeIds: chosen.map((row) => row.id),
        decision,
        runNow: canStart && runNow,
      });
      setConfirming(null);
      setSelected([]);
      setRunNow(false);
      announceDecision(
        notice,
        decidedSentence(decision, result.changed, result.runId !== null),
        result,
      );
    } catch (error) {
      inline.show(error, "The decision could not be recorded.");
    }
  };

  const columns = useMemo<SkyDataGridColumn<SchemaChangeView>[]>(() => {
    const list: SkyDataGridColumn<SchemaChangeView>[] = [];
    if (!entityName) {
      list.push({
        key: "entityName",
        header: "Table",
        width: "14%",
        renderCell: (_value, row) => (
          <SkyText block truncate title={row.entityName}>
            {row.entityName}
          </SkyText>
        ),
      });
    }
    list.push(
      {
        key: "columnName",
        header: "Column",
        width: entityName ? "20%" : "15%",
        renderCell: (_value, row) => (
          <SkyText block truncate weight="medium" title={row.sourceColumn ?? row.columnName}>
            {row.columnName}
          </SkyText>
        ),
      },
      {
        key: "kind",
        header: "Change",
        width: entityName ? "16%" : "13%",
        renderCell: (_value, row) => (
          <div className="min-w-0">
            <SkyText block title={SCHEMA_KIND_HELP[row.kind]}>
              {SCHEMA_KIND_LABELS[row.kind]}
            </SkyText>
            {row.detectedBy === "connector" ? (
              <SkyText size={200} color="muted" block testId={`schema-change-source-${row.id}`}>
                Seen at the source
              </SkyText>
            ) : null}
          </div>
        ),
      },
      {
        key: "typeAfter",
        header: "Types",
        width: entityName ? "22%" : "18%",
        renderCell: (_value, row) => (
          <SkyText size={200} block truncate title={typesText(row.typeBefore, row.typeAfter)}>
            {typesText(row.typeBefore, row.typeAfter)}
          </SkyText>
        ),
      },
      {
        key: "firstSeenAt",
        header: "First seen",
        width: entityName ? "18%" : "16%",
        renderCell: (_value, row) => (
          <SkyLink
            as={Link}
            href={absolutePath(`/runs/${encodeURIComponent(row.firstSeenRunId)}`)}
            testId={`schema-change-run-${row.id}`}
          >
            <span title={instantHoverTitle(row.firstSeenAt, zone)}>
              {formatInstant(row.firstSeenAt, zone, "seconds")}
            </span>
          </SkyLink>
        ),
      },
      {
        key: "status",
        header: "Status",
        width: "24%",
        renderCell: (_value, row) => {
          const presentation = SCHEMA_STATUS_PRESENTATION[row.status];
          return (
            <StatusCell
              badge={
                <SkyBadge
                  appearance="tint"
                  color={presentation.color}
                  testId={`schema-change-status-${row.id}`}
                >
                  {presentation.label}
                </SkyBadge>
              }
            />
          );
        },
      },
    );
    return list;
  }, [entityName, zone]);

  const decisionButton = (decision: SchemaDecision) => (
    <SkyButton
      key={decision}
      size="small"
      appearance={decision === "approve" || decision === "acknowledge" ? "primary" : "secondary"}
      testId={`schema-decide-${decision}`}
      disabled={decide.isPending}
      onClick={() =>
        decision === "widen" || decision === "rebuild"
          ? setConfirming(decision)
          : void submit(decision)
      }
    >
      {SCHEMA_DECISION_LABELS[decision]}
    </SkyButton>
  );

  return (
    <SkyDrawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      position="end"
      size="large"
      testId="schema-change-drawer"
      headerTitle={entityName ? `Schema changes of ${entityName}` : "Schema changes"}
      showCloseButton
    >
      <div className="flex flex-col gap-4">
        <SkyText size={300} color="muted" block>
          Changes to the source columns of {entityName ?? connectionName}, newest first. Names and
          types only. Decisions are carried out by the next import.
        </SkyText>
        {presentKinds.map((kind) => (
          <SkyText key={kind} size={200} color="muted" block testId={`schema-kind-help-${kind}`}>
            {SCHEMA_KIND_LABELS[kind]}: {SCHEMA_KIND_HELP[kind]}
          </SkyText>
        ))}
        <div className="flex flex-wrap items-center gap-2">
          <fieldset className="flex min-w-0 items-center gap-1" aria-label="Show which changes">
            {(runId ? (["import", "open", "all"] as const) : (["open", "all"] as const)).map(
              (value) => (
                <SkyButton
                  key={value}
                  size="small"
                  appearance={view === value ? "secondary" : "subtle"}
                  aria-pressed={view === value}
                  testId={`schema-changes-view-${value}`}
                  onClick={() => {
                    setView(value);
                    setPage(1);
                    setSelected([]);
                  }}
                >
                  {VIEW_LABELS[value]}
                </SkyButton>
              ),
            )}
          </fieldset>
        </div>

        {canWrite && chosen.length > 0 ? (
          <DecisionBar
            decisions={decisions}
            tableCount={tables.length}
            canStart={canStart}
            runNow={runNow}
            onRunNowChange={setRunNow}
            decisionButton={decisionButton}
          />
        ) : null}

        <InlineErrorBar
          error={confirming ? null : inline.error}
          title={inline.error?.retryable ? undefined : "The decision was not recorded"}
          onDismiss={inline.clear}
          testId="schema-decide-error"
        />

        <SkyDataGrid<SchemaChangeView>
          testId="schema-change-grid"
          ariaLabel="Schema changes"
          data={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={log.isLoading}
          selectionMode={canWrite ? "multiple" : "none"}
          selectedRows={selected}
          isRowSelectable={(row) => DECIDABLE.has(row.status)}
          onSelectionChange={(ids) => setSelected(ids.map(String))}
          pagination
          page={page}
          pageSize={pageSize}
          pageSizeOptions={LIST_PAGE_SIZE_OPTIONS}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          totalItems={log.data?.total ?? 0}
          onPageChange={(next) => {
            setPage(next);
            setSelected([]);
          }}
          emptyMessage={emptyLogMessage(view, status)}
        />
      </div>

      {confirming ? (
        <ConfirmDecisionDialog
          confirming={confirming}
          chosen={chosen}
          tableLabel={tableLabel}
          rebuildRunsNow={canStart && runNow}
          pending={decide.isPending}
          error={inline.error}
          onConfirm={() => submit(confirming)}
          onCancel={() => setConfirming(null)}
        />
      ) : null}
    </SkyDrawer>
  );
}
